import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AWS from 'aws-sdk';
import './Authentication.css';
import { useCustomContext } from '../CustomComponents/CustomComponents';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa6';
import UserPool from '../../UserPool';
import { jwtDecode } from 'jwt-decode';
import { insertLog } from '../Logs/InsertLog';


AWS.config.update({
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    region: process.env.REACT_APP_AWS_REGION,
});

const cognitoISP = new AWS.CognitoIdentityServiceProvider();

function SignUp({ onLogin }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [fullName, setFullName] = useState('');
    const [fullNameErrorMessage, setFullNameErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const { showAlert, hud, stopHudRotation } = useCustomContext();

    const location = useLocation();
    const navigate = useNavigate();

    const handleFullNameChange = (e) => {
        const value = e.target.value;
        setFullName(value);
        validateFullName(value);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        validateEmail(value);
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        validatePassword(value);
    };

    const validateFullName = (value) => {
        const isValidLength = value.length > 0;
        if (!isValidLength) {
            setFullNameErrorMessage('Enter your full name.');
        } else {
            setFullNameErrorMessage('');
        }
    };

    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(value);
        if (!isValidEmail) {
            setEmailErrorMessage('Enter a valid email address.');
        } else {
            setEmailErrorMessage('');
        }
    };

   
    
    const validatePassword = (value) => {
        const lengthRegex = /.{8,}/;
        const numberRegex = /\d+/;
        const symbolRegex = /[!@#$%^&*(),.?":{}|<>[\]\\/~`+=\-]/; // Excludes space
        const uppercaseRegex = /[A-Z]+/;
        const lowercaseRegex = /[a-z]+/;

        // Disallow leading or trailing spaces
        const hasNoLeadingOrTrailingSpaces = value === value.trim();

        const isValidPassword =
            lengthRegex.test(value) &&
            numberRegex.test(value) &&
            symbolRegex.test(value) &&
            uppercaseRegex.test(value) &&
            lowercaseRegex.test(value) &&
            hasNoLeadingOrTrailingSpaces;

        if (!isValidPassword) {
            setPasswordErrorMessage(
                'Password must be at least 8 characters and include numbers, symbols (excluding leading/trailing spaces), uppercase and lowercase letters.'
            );
        } else {
            setPasswordErrorMessage('');
        }
    };
    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };
   
    const handleGoogleLogin = () => {
        const clientId = process.env.REACT_APP_COGNITO_CLIENTID;
        const domain = 'us-east-1z0mtkhk91.auth.us-east-1.amazoncognito.com';
        const redirectUri = 'https://refer.sharkunlock.com';
        const responseType = 'code';
        const scope = 'openid profile email';

        const queryParams = new URLSearchParams({
            identity_provider: 'Google',
            redirect_uri: redirectUri,
            response_type: responseType,
            client_id: clientId,
            scope: scope,
            prompt: 'select_account'
        });

        window.location.href = `https://${domain}/oauth2/authorize?${queryParams.toString()}`;

    };
    const getUserDetailsFromCognito = async (cognitoUsername) => {
        try {
            const userData = await cognitoISP.adminGetUser({
                UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                Username: cognitoUsername,
            }).promise();

            console.log('User Details:', userData);

            const attributes = {};
            userData.UserAttributes.forEach(attr => {
                attributes[attr.Name] = attr.Value;
            });

            const email = attributes.email;
            const empInfo = attributes['custom:empInfo'];
            const parsedEmpInfo = JSON.parse(empInfo);
            const accessLevel = parsedEmpInfo.accessLevel;

            localStorage.setItem('isEmployeeApproved', parsedEmpInfo.isEmployeeApproved);
            localStorage.setItem('isEmployeeActive', parsedEmpInfo.isEmployeeActive);
            localStorage.setItem('employeeName', parsedEmpInfo.name);
            localStorage.setItem('accessLevel', accessLevel);
            localStorage.setItem('UserStatus', userData.UserStatus);
            localStorage.setItem('email', email);
            localStorage.setItem('EmpInfo', empInfo);

            const isApproved = parsedEmpInfo.isEmployeeApproved === 'true' || parsedEmpInfo.isEmployeeApproved === true;
            const isActive = parsedEmpInfo.isEmployeeActive === 'true' || parsedEmpInfo.isEmployeeActive === true;
            const userName = email?.split('@')[0];

            if (isApproved && isActive) {
                insertLog(
                    `${userName} successfully signed in with Google and navigated to home screen.`,
                    0,
                    email,
                    hud,
                    stopHudRotation,
                    showAlert
                );
                return { success: true, email };
            }

            // Construct reason and log
            let reason = '';
            if (!isApproved && !isActive) {
                navigate('/AccessDenied')
            } else if (!isApproved) {
                navigate('/AccessDenied')
            } else {
                navigate('/AccessDenied')
            }
        } catch (error) {
            console.error('Failed to get user details:', error);
            return { success: false, reason: 'Unable to fetch user details.' };
        }
    };

    useEffect(() => {
        const handleOAuth = async () => {
            const queryParams = new URLSearchParams(location.search);
            const code = queryParams.get('code');

            if (!code) return;

            const tokenUrl = 'https://us-east-1z0mtkhk91.auth.us-east-1.amazoncognito.com/oauth2/token';
            const body = new URLSearchParams({
                grant_type: 'authorization_code',
                code,
                redirect_uri: 'https://refer.sharkunlock.com',
                client_id: process.env.REACT_APP_COGNITO_CLIENTID,
            });

            try {
                hud('Please Wait...');
                const res = await fetch(tokenUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body,
                });

                const data = await res.json();


                if (data.access_token) {
                    hud("Please Wait...");
                    const idToken = data.id_token;
                    const decoded = jwtDecode(idToken);
                    const email = decoded.email;
                    const providerSub = decoded.sub;
                    const providerUsername = decoded['cognito:username'];

                    console.log('Cognito Username:', providerUsername);
                    let usersWithEmail = [];
                    try {
                        const userList = await cognitoISP
                            .listUsers({
                                UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                Filter: `email = "${email}"`,
                            })
                            .promise();
                        usersWithEmail = userList.Users || [];
                    } catch (err) {
                        console.error('Error listing users with email:', err);
                    }


                    // 2. Identify native user (not EXTERNAL_PROVIDER) and google user
                    let finalUsername = providerUsername;

                    if (usersWithEmail.length >= 1) {
                        const nativeUser = usersWithEmail.find(u => u.UserStatus !== 'EXTERNAL_PROVIDER');
                        const googleUser = usersWithEmail.find(u => u.UserStatus === 'EXTERNAL_PROVIDER');

                        if (nativeUser && googleUser) {
                            console.log('🔁 Both native and Google users found.');

                            try {
                                const userData = await cognitoISP.adminGetUser({
                                    UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                    Username: nativeUser.Username,
                                }).promise();

                                const identitiesAttr = userData.UserAttributes.find(attr => attr.Name === 'identities');
                                let identities = [];

                                if (identitiesAttr && identitiesAttr.Value) {
                                    identities = JSON.parse(identitiesAttr.Value);
                                }

                                const googleAlreadyLinked = identities.some(id => id.providerName === 'Google');

                                if (googleAlreadyLinked) {
                                    console.log('🔗 Native user already linked with Google. Deleting redundant federated Google user.');
                                    await cognitoISP.adminDeleteUser({
                                        UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                        Username: googleUser.Username,
                                    }).promise();
                                    finalUsername = nativeUser.Username;
                                } else if (identities.length >= 5) {
                                    console.warn('⚠️ Cannot link Google: too many linked identities. Deleting redundant federated user.');
                                    await cognitoISP.adminDeleteUser({
                                        UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                        Username: googleUser.Username,
                                    }).promise();
                                    finalUsername = nativeUser.Username;
                                } else {
                                    await cognitoISP.adminLinkProviderForUser({
                                        UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                        DestinationUser: {
                                            ProviderName: 'Cognito',
                                            ProviderAttributeValue: nativeUser.Username,
                                        },
                                        SourceUser: {
                                            ProviderName: 'Google',
                                            ProviderAttributeName: 'Cognito_Subject',
                                            ProviderAttributeValue: providerSub,
                                        },
                                    }).promise();

                                    console.log('✅ Linked Google account to native user');

                                    await cognitoISP.adminDeleteUser({
                                        UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                        Username: googleUser.Username,
                                    }).promise();
                                    console.log('🗑️ Deleted federated Google user after linking');

                                    finalUsername = nativeUser.Username;
                                }
                            } catch (err) {
                                console.error('❌ Error during linking process:', err);
                            }
                        } else if (nativeUser && !googleUser) {
                            console.log('✅ Only native user exists. Using native account.');
                            finalUsername = nativeUser.Username;
                            const emailAttr = nativeUser?.Attributes?.find(attr => attr.Name === 'email');
                            const NativeEmail = emailAttr ? emailAttr.Value : null;
                            console.log("emails:", NativeEmail, email);


                            if (email === NativeEmail) {
                                console.log("values:", NativeEmail, email)
                                try {
                                    // Optional: verify user exists before deleting
                                    const userExists = await cognitoISP.adminGetUser({
                                        UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                        Username: providerUsername,
                                    }).promise().catch(() => null);

                                    if (userExists) {
                                        await cognitoISP.adminDeleteUser({
                                            UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                                            Username: providerUsername,
                                        }).promise();
                                        console.log(`🗑️ Successfully deleted federated Google user: ${providerUsername}`);
                                    } else {
                                        console.log(`ℹ️ Skipped deletion: federated user ${providerUsername} does not exist`);
                                    }
                                } catch (error) {
                                    console.error(`❌ Failed to delete federated Google user: ${providerUsername}`, error);
                                }
                            }
                        }

                        else if (googleUser && !nativeUser) {
                            console.log('✅ Only Google user exists. Using Google federated user.');
                            finalUsername = googleUser.Username;
                        }
                    } else {
                        console.log('🆕 No existing users found with this email. Proceeding as new Google user.');
                        finalUsername = providerUsername;
                    }
                    

                    // 4. Set custom:empInfo if missing
                    try {
                        const userData = await cognitoISP.adminGetUser({
                            UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                            Username: finalUsername,
                        }).promise();

                        const hasEmpInfo = userData.UserAttributes.some(
                            attr => attr.Name === 'custom:empInfo'
                        );

                        if (!hasEmpInfo) {
                            console.log('✅ Set custom:empInfo');
                        }
                    } catch (err) {
                        console.error('❌ Error checking/setting custom attributes:', err);
                    }


                    await getUserDetailsFromCognito(providerUsername);

                    localStorage.setItem('isLoggedIn', 'true');
                    onLogin(email);
                    stopHudRotation();
                    navigate('/Referrals');
                    window.history.replaceState(null, '', window.location.pathname);

                } else {
                    console.error('Token exchange failed:', data);
                    stopHudRotation();
                }
            } catch (err) {
                console.error('Error exchanging code:', err);
                stopHudRotation();
            }
            finally {
                stopHudRotation();
            }
        };
        handleOAuth();

    }, [location, onLogin, navigate]);




    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous error messages
        setEmailErrorMessage('');
        setPasswordErrorMessage('');
        setFullNameErrorMessage('');

        // Validate fields
        validateFullName(fullName);
        validateEmail(email);
        validatePassword(password);

        // Check for empty fields
        if (!fullName) {
            setFullNameErrorMessage('Enter your full name.');
        }
        if (!email) {
            setEmailErrorMessage('Enter your email address.');
        }
        if (!password) {
            setPasswordErrorMessage('Enter your password.');
        }

        // Exit early if any field is empty or has errors
        if (!fullName || !email || !password || fullNameErrorMessage || emailErrorMessage || passwordErrorMessage) {
            return;
        }

        hud("Please Wait...");

        // Step 1: Check if user already exists in Cognito
        try {
            const response = await cognitoISP.listUsers({
                UserPoolId: process.env.REACT_APP_COGNITO_USERPOOLID,
                Filter: `email = "${email}"`,
            }).promise();

            const existingUsers = response.Users || [];
            if (existingUsers.length > 0) {
                const existingUser = existingUsers[0];

                if (existingUser.UserStatus === 'EXTERNAL_PROVIDER') {
                    stopHudRotation();
                    showAlert("User already exists with Google Provider.", [
                        {
                            label: 'Sign in with Google',
                            onClick: () => {
                                handleGoogleLogin();
                            },
                            color: 'var(--buttonColor)',
                        },
                        {
                            label: 'Cancel',
                            onClick: () => {
                                navigate('/');
                            },
                            color: 'var(--error)',
                        },
                    ]);
                    return;
                } else {
                    stopHudRotation();
                    showAlert("User already exists with this email address.", [
                        {
                            label: 'OK',
                            onClick: () => { },
                            color: 'var(--buttonColor)',
                        },
                    ]);
                    return;
                }
            }
        } catch (error) {
            console.error("Error checking existing user:", error);
            stopHudRotation();
            showAlert("Something went wrong. Please try again.", [
                {
                    label: 'OK',
                    onClick: () => { },
                    color: 'var(--buttonColor)',
                },
            ]);
            return;
        }

        // Step 2: If not exists, proceed with registration
        const attributes = [
            {
                Name: 'custom:empInfo',
                Value: JSON.stringify({
                    name: fullName,
                    accessLevel: 1,
                    isEmployeeApproved: false,
                    isEmployeeActive: false
                })
            }
        ];

        UserPool.signUp(email, password, attributes, null, (err, data) => {
            stopHudRotation();
            if (err) {
                showAlert(err.message, [
                    {
                        label: 'Ok',
                        onClick: () => { },
                        color: 'var(--buttonColor)',
                    },
                ]);
            } else {
                showAlert('Verification code sent to your registered email address.', [
                    {
                        label: 'Ok',
                        onClick: () => {
                            setEmail('');
                            setPassword('');
                            setFullName('');
                            navigate('/VerifyEmail', { state: { email } });
                        },
                        color: 'var(--buttonColor)',
                    },
                ]);
            }
        });
    };


    return (
        <div className='container d-flex flex-column align-items-center login-hub'>
            <span className="heading-span mb-2"><img src="/logo.png" alt="email" /></span>
            <span className='font-weight-bold mb-3 logo'>
                Welcome to the Referral Manager. Please sign up for your account.
            </span>
            <div className='input-container mb-3 w-100'>
                <div className='d-flex justify-content-center w-100'>
                    <form onSubmit={handleSubmit} className='form-container'>
                        <div className='form-group mb-3'>
                            <input
                                className={`form-control ${fullNameErrorMessage ? 'is-invalid' : fullName ? 'is-valid' : ''} inputs`}
                                type='text'
                                placeholder='Full Name'
                                value={fullName}
                                onChange={handleFullNameChange}
                            />
                            {fullNameErrorMessage && <div className='invalid-feedback error-message'>{fullNameErrorMessage}</div>}
                        </div>
                        <div className='form-group mb-3'>
                            <input
                                className={`form-control ${emailErrorMessage ? 'is-invalid' : email ? 'is-valid' : ''} inputs`}
                                type='email'
                                placeholder='Email'
                                value={email}
                                onChange={handleEmailChange}
                            />
                            {emailErrorMessage && <div className='invalid-feedback error-message'>{emailErrorMessage}</div>}
                        </div>
                        <div className='form-group'>
                            <div className='password-container'>
                                <input
                                    className={`form-control ${passwordErrorMessage ? 'is-invalid' : password ? 'is-valid' : ''} inputs`}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder='Password'
                                    value={password}
                                    onChange={handlePasswordChange}
                                /><span
                                    className='password-toggle-icon'
                                    onClick={toggleShowPassword}
                                    style={{ right: passwordErrorMessage ? '40px' : '10px' }}
                                >
                                    {showPassword ? <FaRegEye className='pass-eye' /> : <FaRegEyeSlash />}
                                </span>

                                {passwordErrorMessage && <div className='invalid-feedback error-message'>{passwordErrorMessage}</div>}
                            </div>
                        </div>
                        <button className='btn btn-primary submit btn-block mt-4 submit-button' type='submit'>
                            Create Account
                        </button>
                    </form>
                </div>
                <div className="form-group mb-3 text-center" style={{ paddingTop: '10px' }}>
                    <p>
                        Already have an account?{' '}
                        <Link
                            to="/"
                            className="text-decoration-underline custom-link2"
                        >
                            Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default SignUp;